import { Ref } from 'vue';
import {
  BasicCollection,
  ShareableContentCollection,
  ContentCollection,
} from '@ilteducation/content-collections-types';
import { ContentReference, ContentsByReferences } from './types';
import { type StoreGeneric, storeToRefs } from 'pinia';
import { useUserContext } from '@ilteducation/ilt-auth';
import { ContentTypes } from '@ilteducation/content-registry-types';

export const isCollectionOwner = (collection: BasicCollection) => {
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  // TODO #shareModule check it, removed here checking by profile
  return (collection.owners ?? []).some((owner) => owner.userId === user.value.uid);
};

export const isCollectionSubscriber = (collection: BasicCollection): boolean => {
  // This is a known type-checking bug that was present in older versions of Pinia, specifically when using storeToRefs() with a mix of ref,
  // computed, and methods in the setup store syntax. Newer versions of Pinia have fixed these type inference issues,
  // allowing the storeToRefs() helper to correctly preserve the types of your properties.
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  if (collection.subscribers === undefined || collection.subscribers.length === 0) {
    return false;
  }
  return collection.subscribers.some((s) => s.id === user.value.uid);
};

export const isCollectionMember = (collection: BasicCollection): boolean => {
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  if (collection.members === undefined || collection.members.length === 0) {
    return false;
  }
  return collection.members.some((s) => s.id === user.value.uid);
};

export const isHistoryCollection = (id: string): boolean => id.startsWith('history');

export const isSharedCollection = (id: string): boolean => id.startsWith('shared');

export const isCollectionCreator = (collection: BasicCollection) => {
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  return collection?.creator?.userId === user.value.uid;
};

export const isCuratedCollection = (collection: BasicCollection): boolean =>
  collection?.owners === undefined && !isSharedCollection(collection.id);

export const isFullProjection = (
  collection: BasicCollection | ContentCollection | ShareableContentCollection,
): collection is ContentCollection => 'contents' in collection;

export const addContentsReferences = (
  contents: ContentsByReferences,
  newReferences: ContentReference[],
): ContentsByReferences => {
  const referencesSet = new Set();
  const contentReferences: ContentReference[] = [...newReferences, ...contents.references].filter((r) => {
    const stringReference = stringifyReference(r);
    const exists = referencesSet.has(stringReference);
    referencesSet.add(stringReference);
    return !exists;
  });

  return {
    type: 'ContentsByReferences',
    references: contentReferences,
  };
};

export const removeContentsReferences = (
  contents: ContentsByReferences,
  referencesToRemove: ContentReference[],
): ContentsByReferences => {
  const referencesSet = new Set(referencesToRemove.map((r) => stringifyReference(r)));
  const contentReferences: ContentReference[] = contents.references.filter((r) => {
    const stringReference = stringifyReference(r);
    return !referencesSet.has(stringReference);
  });

  return {
    type: 'ContentsByReferences',
    references: contentReferences,
  };
};

/**
 * Duplicated logic from the content-collections service, to not have to send another call.
 * @param collection
 */
export const getCollectionSharedSettingsId = (collection: ContentCollection | BasicCollection) => collection.id;

const shareRelatedCollectionsPrefixes: string[] = ['shared-content:', 'shared-by-me:'];
const prefixes = ['favourites', 'history', 'downloaded', ...shareRelatedCollectionsPrefixes];
const predefinedRegex = new RegExp(`^(${prefixes.join('|')})(:|\\b)`, 'i');

export const isPredefinedCollection = (collectionId: Ref<string>): boolean => predefinedRegex.test(collectionId.value);
export const isShareRelatedCollection = (collectionId: Ref<string>): boolean => {
  return shareRelatedCollectionsPrefixes.some((prefix) => collectionId.value.startsWith(prefix));
};
export const getDateFromCollectionByKey = (collection: BasicCollection, key: keyof BasicCollection): string => {
  if (!(key in collection)) {
    return '';
  }
  const dateToFormat = new Date(collection[key] as string);
  return dateToFormat.toLocaleDateString('en-CA');
};

export const parseReference = (cursor: string): ContentReference => {
  const [type, id, categoryId, areaId] = cursor.split(':');
  if (!type || !id) {
    throw new Error('Invalid reference string');
  }
  return { id, type: type as ContentTypes, categoryId, areaId };
};
export const stringifyReference = (reference: ContentReference): string => {
  const parts = [reference.type, reference.id];
  if ('categoryId' in reference && reference.categoryId !== undefined) {
    parts.push(reference.categoryId);
  }
  if ('areaId' in reference && reference.areaId !== undefined) {
    parts.push(reference.areaId);
  }
  return parts.join(':');
};

export const isSameReference = <T extends ContentReference>(ref1: T, ref2: T): boolean => {
  return stringifyReference(ref1) === stringifyReference(ref2);
};
